import { parseScanned, type ScannedCode } from "./connect-code";

/**
 * Reading a connect QR (TNG-93) from the camera or a photo.
 *
 * Android Chrome has a native `BarcodeDetector`. iOS Safari and home-screen
 * web apps do not, so there the `barcode-detector` ponyfill decodes with
 * zxing-wasm. Both are behind one interface and the ponyfill is loaded only
 * when the native one is missing, the first time somebody scans.
 *
 * The wasm binary is served from this app, not from a CDN: the ponyfill
 * fetches it from jsDelivr by default, which would make scanning depend on a
 * third party and tell it who scans when. Vite emits it under /assets with a
 * content hash in its name, so every release that changes it gets a new URL.
 */

export interface DetectedCode {
  rawValue: string;
}

export interface QrDetector {
  detect(
    source: CanvasImageSource | ImageBitmapSource,
  ): Promise<DetectedCode[]>;
}

interface NativeDetectorConstructor {
  new (options: { formats: string[] }): QrDetector;
  getSupportedFormats(): Promise<string[]>;
}

async function nativeDetector(): Promise<QrDetector | null> {
  const native = (globalThis as { BarcodeDetector?: NativeDetectorConstructor })
    .BarcodeDetector;
  if (!native) return null;
  try {
    const formats = await native.getSupportedFormats();
    return formats.includes("qr_code")
      ? new native({ formats: ["qr_code"] })
      : null;
  } catch {
    return null;
  }
}

let detectorPromise: Promise<QrDetector> | null = null;

/** The detector, created once per page. */
export function loadQrDetector(): Promise<QrDetector> {
  detectorPromise ??= (async () => {
    const native = await nativeDetector();
    if (native) return native;
    const [{ BarcodeDetector, setZXingModuleOverrides }, wasm] =
      await Promise.all([
        import("barcode-detector/ponyfill"),
        import("zxing-wasm/reader/zxing_reader.wasm?url"),
      ]);
    setZXingModuleOverrides({
      locateFile: (path: string, prefix: string) =>
        path.endsWith(".wasm") ? wasm.default : prefix + path,
    });
    return new BarcodeDetector({ formats: ["qr_code"] });
  })().catch((error: unknown) => {
    // A failed load (offline on first use) may succeed on the next attempt.
    detectorPromise = null;
    throw error;
  });
  return detectorPromise;
}

/** The first connect code among what the detector saw, if any. */
export function firstCode(detected: DetectedCode[]): ScannedCode | null {
  for (const item of detected) {
    const code = parseScanned(item.rawValue);
    if (code) return code;
  }
  return null;
}

/**
 * The detector threw on every frame for a while: whatever is wrong is not one
 * unreadable frame, and scanning on would only spin without a word.
 */
export class DetectorFailedError extends Error {
  constructor(cause: unknown) {
    super("The code reader failed on every frame", { cause });
    this.name = "DetectorFailedError";
  }
}

/**
 * Runs the detector over the video until it finds a connect code or the
 * signal aborts. One frame at a time with a pause between them, so a slow
 * wasm decode never piles up calls. A QR that is not a connect code is
 * ignored and scanning goes on. A frame the detector throws on is skipped,
 * but `maxConsecutiveErrors` of them in a row (about five seconds at the
 * default pace) reject with DetectorFailedError.
 */
export async function detectLoop({
  detector,
  source,
  signal,
  intervalMs = 200,
  ready = () => true,
  maxConsecutiveErrors = 25,
}: {
  detector: QrDetector;
  source: CanvasImageSource;
  signal: AbortSignal;
  intervalMs?: number;
  /** False while the video has no frame to read yet. */
  ready?: () => boolean;
  maxConsecutiveErrors?: number;
}): Promise<ScannedCode | null> {
  let errors = 0;
  while (!signal.aborted) {
    if (ready()) {
      try {
        const code = firstCode(await detector.detect(source));
        errors = 0;
        if (code && !signal.aborted) return code;
      } catch (error) {
        // A frame the decoder could not read. The next one may do, unless
        // none has for a while.
        errors += 1;
        if (errors >= maxConsecutiveErrors && !signal.aborted) {
          throw new DetectorFailedError(error);
        }
      }
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return null;
}

/** Decodes a photo the person took or picked. */
export async function decodePhoto(
  detector: QrDetector,
  file: Blob,
): Promise<ScannedCode | null> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return firstCode(await detector.detect(image));
  } finally {
    URL.revokeObjectURL(url);
  }
}

export type CameraFailure = "denied" | "unavailable" | "unsupported";

export function cameraFailure(error: unknown): CameraFailure {
  const name = error instanceof DOMException ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "denied";
  if (name === "NotSupportedError" || name === "TypeError")
    return "unsupported";
  return "unavailable";
}

/**
 * The rear camera, asked for from a tap: iOS refuses a camera that no user
 * gesture asked for, and a page that opens the camera on its own is one the
 * person did not expect.
 */
export async function openRearCamera(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new DOMException("No camera API", "NotSupportedError");
  }
  return navigator.mediaDevices.getUserMedia({
    audio: false,
    video: { facingMode: { ideal: "environment" } },
  });
}

export function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}
